import React, { useCallback, useState, useMemo } from 'react'
import { Box, Typography, TypographyProps } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import useGTM from '@elgorditosalsero/react-gtm-hook'
import defaultAxios from 'axios'
import useAxios from '../../../hooks/useAxios'
import useCommonTRResumeUploadPayload from '../../../hooks/useCommonTRResumeUploadPayload'
import ComplianceNoteProvider from '../../../hooks/useComplianceNote'
import useDynamicSearchParam from '../../../hooks/useDynamicSearchParam'
import useLeadActions from '../../../hooks/useLeadActions'
import { EventNames, useStatsEvent } from '../../../hooks/useStatsEvent'
import { useTopResumeUpload } from '../../../hooks/useTopResumeUpload'
import useUser from '../../../hooks/useUser'
import { Endpoints } from '../../../types/constants'
import {
  CommonPayload,
  DesktopPayload,
  IDesktopUploadPayload,
  IMobileUploadPayload,
  IResumeUpload,
  MobilePayload,
  PartnerApiResponse,
  UploadGTMEvents,
} from '../../../types/widget'
import {
  isUploadSucess,
  toFormData,
  useResumeUploadGTMPayload,
  useWidgetQueries,
} from '../../../utils/widget'
import {
  ResumeUploadButtonCallback,
  ResumeUploadButtonEventType,
} from '../../Buttons/ResumeUpload'
import ResumeUploadStatus from '../../Modals/ResumeUploadStatus'
import DesktopUploadWidget from './DesktopUploadWidget'
import EmailSubmitModal from './EmailSubmitModal'
import MobileEmailSubmit from './MobileEmailSubmit'
import MobileUploadWidget from './MobileUploadWidget'
import {
  useMixpanelCTAClickTracking,
  useMpEventType,
} from '../../../utils/mixpanel'
import { MixpanelCTALinkActions, MixpanelEvents } from '../../../types/mixpanel'
import DesktopUploadWidgetV2 from './DesktopUploadWidgetV2'
import MobileUploadWidgetV2 from './MobileUploadWidgetV2'
import { useFETelemetry } from '@talentinc/gatsby-theme-ecom/hooks/useTelemetry'

type Props = {
  options: IResumeUpload
}

const V2_CLASSNAME = 'centered-2-column-variant'
const DESKTOP_HIDDEN_CLASSNAME = 'desktop-hidden'

export default function TRResumeUpload({ options }: Props) {
  const { mobileWidget } = options
  const [resumeFileOnlyApiStatus, setResumeFileOnlyApiStatus] = useState<
    'idle' | 'submitting' | 'error' | 'success'
  >('idle')
  const [resumeApiStatus, setResumeApiStatus] = useState<
    'idle' | 'submitting' | 'error' | 'success'
  >('idle')
  const [variant, setVariant] = useState<'upload' | 'email'>('upload')
  const { isEmptyFile, setResumeFile, setChecksum, clearState } =
    useLeadActions()
  const { classes, cx } = useStyles()
  const { leadEmailExists } = useTopResumeUpload()
  const commonUploadPayload = useCommonTRResumeUploadPayload()
  const axios = useAxios({ withPartnerAuth: true })
  const { mutate } = useStatsEvent()
  const { email } = useUser()
  const emailQueryParam = useDynamicSearchParam('email')
  const { sendDataToGTM } = useGTM()
  const { isDesktop } = useWidgetQueries()
  const gtmPayload = useResumeUploadGTMPayload()
  const handleMpCTAClickTracking = useMixpanelCTAClickTracking()
  const telemetry = useFETelemetry()
  const mpEventType = useMpEventType()
  const { css, showMobileButton } = options
  const isV2: boolean =
    options.isV2 || (Array.isArray(css) && css.some((entry) => entry.className === V2_CLASSNAME))
  const isDesktopHidden: boolean =
    Array.isArray(css) &&
    css.some((entry) => entry.className === DESKTOP_HIDDEN_CLASSNAME)

  const DesktopWidget = isV2 ? DesktopUploadWidgetV2 : DesktopUploadWidget
  const MobileWidget = useMemo(() => {
    if (showMobileButton) return MobileUploadWidgetV2
    if (isV2) return DesktopUploadWidgetV2
    return MobileUploadWidget
  }, [showMobileButton, isV2])

  const handleSendEmail = () => {
    setVariant('email')
    setResumeFileOnlyApiStatus('success')
  }

  const fireMpResumeAddedEvent = useCallback(() => {
    handleMpCTAClickTracking(null, MixpanelCTALinkActions.ResumeUploadAction, {
      eventName: MixpanelEvents.ResumeAdded,
      trackingData: {
        event_type: mpEventType,
      },
      trackOn: null,
    })
  }, [handleMpCTAClickTracking, mpEventType])

  const handleFileOnlyUpload = useCallback<ResumeUploadButtonCallback>(
    async (event: ResumeUploadButtonEventType) => {

      telemetry.track({
        event: 'try_upload_resume',
        properties: {
          event_type: mpEventType,
        }
      })

      // More satefy: Avoid any redundant submissions
      if (resumeFileOnlyApiStatus === 'submitting') {
        return
      }

      // We allow uploading when there's a file format error.
      // So, reset any stale states.
      setResumeFileOnlyApiStatus('idle')
      setChecksum('')

      let file: File | null = null

      if ('files' in event.target && event.target.files?.[0]) {
        file = event.target.files[0]
      } else if ('dataTransfer' in event && event.dataTransfer.files?.[0]) {
        file = event.dataTransfer.files[0]
        // On MacOS (at least) dropping a file like this without preventDefault
        // will cause the document to open in a new tab.
        event.preventDefault()
      }

      if (file && setResumeFile(file) && !(await isEmptyFile(file))) {
        const payload = {
          resume_file: file,
          file_name: file.name,
          lead_source_id: DesktopPayload.LeadSourceId,
          lead_type_id: DesktopPayload.LeadTypeId,
          tags: JSON.stringify({
            utm_lead: CommonPayload.UtmLead,
            referring_url: window?.location.href || '',
          }),
        } satisfies IDesktopUploadPayload

        const data = toFormData(payload, commonUploadPayload)

        setResumeFileOnlyApiStatus('submitting')

        try {
          const {
            data: {
              status: { code },
              data: { checksum },
            },
          } = await axios.post<PartnerApiResponse>(
            Endpoints.TRDesktopResumeUpload,
            data
          )

          if (isUploadSucess(code)) {
            switch (code) {
              case '202': {
                mutate(EventNames.frontendResumeFileOnlyRepeatUpload)
                sendDataToGTM({
                  event: UploadGTMEvents.ResumeUploadDuplicate,
                  ...gtmPayload,
                })
                break
              }
              case '200': {
                mutate(EventNames.frontendResumeFileOnlyUpload)
                break
              }
              default:
                break
            }
            setResumeFileOnlyApiStatus('success')

            if (checksum) {
              setChecksum(checksum)
            }

            if (isDesktop) {
              sendDataToGTM({
                event: UploadGTMEvents.ResumeFileUploadSuccess,
              })
            }

            fireMpResumeAddedEvent()
          } else {
            mutate(EventNames.frontendResumeFileOnlyUploadFailure)

            sendDataToGTM({
              event: UploadGTMEvents.ResumeUploadError,
              ...gtmPayload,
            })

            setResumeFileOnlyApiStatus('error')
          }
        } catch (e: unknown) {
          if (defaultAxios.isAxiosError<Partial<PartnerApiResponse>>(e)) {
            const { response } = e

            if (response) {
              const {
                data: { status, data },
              } = response

              if (isUploadSucess(status?.code, status?.message)) {
                setResumeFileOnlyApiStatus('success')
                mutate(EventNames.frontendResumeFileOnlyUpload)
                if (data?.checksum) {
                  setChecksum(data?.checksum)
                }

                if (isDesktop) {
                  sendDataToGTM({
                    event: UploadGTMEvents.ResumeFileUploadSuccess,
                  })
                }

                // Fire 'Resume Upload Duplicate' for 409s
                sendDataToGTM({
                  event: UploadGTMEvents.ResumeUploadDuplicate,
                  ...gtmPayload,
                })

                fireMpResumeAddedEvent()
                return
              } else {
                mutate(EventNames.frontendResumeFileOnlyUploadFailure)

                sendDataToGTM({
                  event: UploadGTMEvents.ResumeUploadError,
                  ...gtmPayload,
                })
              }
            }
          }

          setResumeFileOnlyApiStatus('error')
        }
      } else {
        sendDataToGTM({
          event: UploadGTMEvents.ResumeUploadError,
          ...gtmPayload,
        })
      }
    },
    [
      resumeFileOnlyApiStatus,
      setChecksum,
      setResumeFile,
      isEmptyFile,
      commonUploadPayload,
      axios,
      isDesktop,
      fireMpResumeAddedEvent,
      mutate,
      sendDataToGTM,
      gtmPayload,
    ]
  )

  const handleUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0]

      if (
        selectedFile &&
        setResumeFile(selectedFile) &&
        !(await isEmptyFile(selectedFile))
      ) {
        const payload = {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          email: (email ?? emailQueryParam)!,
          utm_source: MobilePayload.UtmSource,
          utm_campaign: MobilePayload.UtmCampaign,
          utm_medium: MobilePayload.UtmMedium,
          utm_content: MobilePayload.UtmContent,
          lead_source_id: MobilePayload.LeadSourceId,
          lead_type_id: MobilePayload.LeadTypeId,
          resume_file: selectedFile,
          tags: JSON.stringify({
            utm_source: MobilePayload.UtmSource,
            utm_campaign: MobilePayload.UtmCampaign,
            utm_medium: MobilePayload.UtmMedium,
            utm_content: MobilePayload.UtmContent,
          }),
        } satisfies IMobileUploadPayload

        setResumeApiStatus('submitting')

        try {
          const {
            data: {
              status: { code },
            },
          } = await axios.post<PartnerApiResponse>(
            Endpoints.TRMobileResumeUpload,
            toFormData(payload, commonUploadPayload)
          )

          if (isUploadSucess(code)) {
            mutate(EventNames.frontendResumeUpload)
            setResumeApiStatus('success')

            fireMpResumeAddedEvent()
          } else {
            mutate(EventNames.frontendResumeUploadFailure)

            sendDataToGTM({
              event: UploadGTMEvents.ResumeUploadError,
              ...gtmPayload,
            })

            setResumeApiStatus('error')
          }

          if (code === '202') {
            sendDataToGTM({
              event: UploadGTMEvents.ResumeUploadDuplicate,
              ...gtmPayload,
            })
          }
        } catch (e) {
          if (defaultAxios.isAxiosError<Partial<PartnerApiResponse>>(e)) {
            const { response } = e

            if (response) {
              const {
                data: { status },
              } = response

              if (isUploadSucess(status?.code, status?.message)) {
                setResumeApiStatus('success')
                mutate(EventNames.frontendResumeUpload)

                // Fire 'Resume Upload Duplicate' for 409s
                sendDataToGTM({
                  event: UploadGTMEvents.ResumeUploadDuplicate,
                  ...gtmPayload,
                })

                fireMpResumeAddedEvent()
                return
              } else {
                mutate(EventNames.frontendResumeUploadFailure)

                sendDataToGTM({
                  event: UploadGTMEvents.ResumeUploadError,
                  ...gtmPayload,
                })
              }
            }
          }

          setResumeApiStatus('error')
        }
      } else {
        sendDataToGTM({
          event: UploadGTMEvents.ResumeUploadError,
          ...gtmPayload,
        })
      }
    },
    [
      setResumeFile,
      isEmptyFile,
      email,
      emailQueryParam,
      axios,
      commonUploadPayload,
      mutate,
      fireMpResumeAddedEvent,
      sendDataToGTM,
      gtmPayload,
    ]
  )

  const resetFileOnlyState = useCallback(() => {
    setVariant('upload')
    clearState()
    setResumeFileOnlyApiStatus('idle')
  }, [clearState])

  const resetState = useCallback(() => {
    setVariant('upload')
    clearState()
    setResumeApiStatus('idle')
  }, [clearState])

  return (
    <ComplianceNoteProvider>
      {/* Common stuff start */}

      {/* File only step 2 */}
      {/* @TODO: Make the literals more explicit */}
      {resumeFileOnlyApiStatus === 'success' && (
        <EmailSubmitModal variant={variant} isV2={isV2} resetState={resetFileOnlyState} />
      )}
      {leadEmailExists && (
        <ResumeUploadStatus
          open={resumeApiStatus === 'success'}
          onClose={resetState}
          status={resumeApiStatus}
        />
      )}

      {/* Error modals */}
      <ResumeUploadStatus
        open={resumeFileOnlyApiStatus === 'error'}
        onClose={resetFileOnlyState}
        status={resumeFileOnlyApiStatus}
      />
      <ResumeUploadStatus
        open={resumeApiStatus === 'error'}
        onClose={resetState}
        status={resumeApiStatus}
      />

      {/* Common stuff end */}
      <Box
        className={cx({
          [classes.hideDesktop]: true,
          [classes.hidden]: isV2 && isDesktopHidden,
        })}
      >
        <DesktopWidget
          onChange={handleFileOnlyUpload}
          handleSendEmail={handleSendEmail}
          resumeApiStatus={resumeFileOnlyApiStatus}
        />
      </Box>

      <Box
        className={cx(
          classes.hideMobile,
          mobileWidget === 'Upload Widget' && classes.mobileWidget
        )}
      >
        {mobileWidget === 'Email Submit Form' && (
          <MobileEmailSubmit css={css} />
        )}

        {mobileWidget === 'Upload Widget' && (
          <>
            <MobileWidget
              onChange={leadEmailExists ? handleUpload : handleFileOnlyUpload}
              handleSendEmail={handleSendEmail}
              resumeApiStatus={
                leadEmailExists ? resumeApiStatus : resumeFileOnlyApiStatus
              }
            />
          </>
        )}
      </Box>
    </ComplianceNoteProvider>
  )
}

export const useStyles = makeStyles()((theme) => ({
  hideDesktop: {
    [theme.breakpoints.down(theme.breakpoints.values.tablet)]: {
      display: 'none',
    },
  },
  hidden: {
    display: 'none',
  },
  hideMobile: {
    [theme.breakpoints.up(theme.breakpoints.values.tablet)]: {
      display: 'none',
    },
  },
  mobileWidget: {
    width: '100%',
  },
}))

type CaptionedTypographyProps = Omit<TypographyProps, 'component' | 'variant'>

export function CaptionedTypography(
  typographyProps: CaptionedTypographyProps
): JSX.Element {
  return <Typography component="span" variant="caption" {...typographyProps} />
}
